import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useMemo } from 'react'
import type { Chart, ChartDatum } from '../types'
import { chartAriaLabel, chartTable } from './a11y'
import { isOwn } from '../brandSelection'
import { seriesStyles } from './emphasis'
import { withTrendLabels } from './labels'
import { PARTIAL_BAR, PARTIAL_DASH, PARTIAL_OPACITY, isDerivedIncomplete, isIncompletePoint, isPartialPoint, missingDays, missingDaysText, partialKey, splitPartialLine } from './partial'
import { isSupportedChartType, unsupportedChartMessage } from './registry'
import { OWN_BRANDS, axisTick, brand, brandColor, dataPalette, formatNumber, gridStroke, metricColor } from '../theme'
import { chartAxisFormatter, formatShare, formatShareValue, isPercentUnit } from '../format'
import { CustomTooltip } from './CustomTooltip'
import { InfoTip } from '../components/InfoTip'
import { NO_SALES_IN_PERIOD } from '../filterSales'

// Every value plotted on a chart's axis, so the default axis formatter can pick decimals that
// keep the ticks Recharts draws distinct (format.ts: chartAxisFormatter). Only the plotted series
// count (`chart.series`, else `value`): MPD-132 attempt 2 — a datum also carries numeric metadata
// such as MP-OS-207's `missing_days`, and a count of 7 next to shares of 0.004 % widened the tick
// grid to whole numbers, so every share tick read "0".
function plottedValues(chart: Chart): number[] {
  const keys = chart.series?.length ? chart.series : ['value']
  const out: number[] = []
  for (const d of chart.data) {
    for (const key of keys) {
      const v = d[key]
      if (typeof v === 'number' && Number.isFinite(v)) out.push(v)
    }
  }
  return out
}

// The axis tickFormatter, built once per chart. Recharts calls tickFormatter(value, index), so a
// caller's formatter is wrapped to receive the value only: a formatter with an optional second
// parameter (formatAxisCompact's `ticks`) would otherwise get the tick index and throw (MPD-46 #1).
function useAxisFormatter(chart: Chart, custom?: (value: number | string) => string) {
  return useMemo(() => custom ? (value: number | string) => custom(value) : chartAxisFormatter(plottedValues(chart)),
    [chart, custom])
}

const ANIM = 600

// MPD-133 Info: a single series is named by its unit in the tooltip; a percent unit would read
// "%: <0.01 %", so it is named "Share" ("% of All White" → "Share of All White") and the value
// carries the "%" once.
export const singleSeriesName = (unit: string): string =>
  isPercentUnit(unit) ? `Share${unit.trim().slice(1).trimEnd()}` : unit

// MPD-132 — a series in a multi-series chart that sold nothing in the shown period would be a
// silent legend entry with no visible line. Its legend entry is muted and says so instead. A
// series with a verified 0 (MP-OS-207) in every bucket, all complete, sold nothing; a series with
// no value at all has no data. MPD-133 L8: a series without a positive value but with a missing
// (null) or incomplete bucket is "Data incomplete": the missing days could hold sales.
export type EmptySeries = 'no-sales' | 'no-data' | 'incomplete'
export function emptySeries(data: ChartDatum[], series: readonly string[]): Map<string, EmptySeries> {
  const out = new Map<string, EmptySeries>()
  for (const key of series) {
    const values = data.map((d) => d[key]).filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
    if (values.some((v) => v !== 0)) continue
    const complete = values.length === data.length && !data.some(isIncompletePoint)
    out.set(key, !values.length ? 'no-data' : complete ? 'no-sales' : 'incomplete')
  }
  return out
}
const EMPTY_SERIES_TEXT: Record<EmptySeries, string> = {
  'no-sales': NO_SALES_IN_PERIOD, 'no-data': 'No data in period', incomplete: 'Data incomplete',
}
function legendFormatter(empty: Map<string, EmptySeries>, labels?: Record<string, string>) {
  return (value: string) => {
    const state = empty.get(value)
    if (!state) return value
    return (
      <span className="legend-empty" title={labels?.[value]} style={{ color: brand.greyText, fontStyle: 'italic' }}>
        {value} · {EMPTY_SERIES_TEXT[state]}
      </span>
    )
  }
}

// palette 'brand' → own=accent, competitors=chart-2/3, others=chart-4 (brand
// charts: market share, brand ranking, compare). 'metric' → one accent colour for
// a single-metric ranking (city, payment, time). Default 'metric'.
type Palette = 'brand' | 'metric'
// MP-OS-188 bug #1 — this used to compare `ownBrands.some((b) => b === name.toUpperCase())`,
// which only matched when the caller's own-brand list was already fully uppercased (true of
// the module-level OWN_BRANDS, built from theme.ts, but NOT of LandingHero's resolvedOwnBrands,
// which keeps the server/tenant brands in their original case). A mixed-case own brand like
// "General" or "Björkkrans" then matched nothing and silently fell back to the 'metric'
// palette. Reuse `isOwn` (brandSelection.ts) — the same case/whitespace-insensitive compare
// `seriesStyles` already relies on for its own per-series colouring — instead of writing a
// second normalizer.
export function chartPalette(chart: Chart, requested?: Palette, ownBrands: readonly string[] = OWN_BRANDS): Palette {
  if (requested) return requested
  const hasOwnBrand = chart.series?.some((name) => isOwn(name, ownBrands))
  return hasOwnBrand ? 'brand' : 'metric'
}
const cellColor = (name: string, palette: Palette): string =>
  palette === 'brand' ? brandColor(name) : metricColor
// In a resizable dashboard card we fill the card (height 100%); in the chat we
// use a natural fixed height.
// `animate` defaults to !fill (chat charts animate, dashboard cards don't). The
// landing hero passes animate={false} so the line is drawn immediately — react-
// smooth's animation never ticks under headless screenshot / html2canvas export.
// `onPointClick` makes the line chart's points annotatable (MP-OS-136): Olof asked to click a
// peak and say what happened there — "i den gamla versionen kunde man klicka på en punkt och
// lägga en kommentar, då ser du att det har hänt något där". It receives the bucket's x-axis
// label; the caller turns that into dates (landing.bucketRange). `noteBuckets` marks the
// buckets that already carry a note, so the explanation is visible on the curve itself and not
// only in the list below it.
type Props = {
  chart: Chart
  fill?: boolean
  animate?: boolean
  palette?: Palette
  // The own-brand set used for emphasis (colour, weight, legend order). Defaults to the
  // static tenant config (theme.OWN_BRANDS); callers whose own brand is only known from a
  // server response (e.g. a manufacturer with no static brand registry) must pass the
  // resolved set so the own brand keeps its reference styling instead of silently falling
  // back to the generic multi-series palette.
  ownBrands?: readonly string[]
  emptyMessage?: string
  onPointClick?: (bucket: string) => void
  noteBuckets?: ReadonlyMap<string, string>
  tooltipLabels?: Record<string, string>
  // Opt-in value formats (e.g. SEK charts: "60 M" ticks, "1.2 M SEK" tooltips). Unset keeps
  // the default compact-thousands axis and full-number tooltip every other chart uses.
  axisFormatter?: (value: number | string) => string
  valueFormatter?: (value: number) => string
}
const H = (fill: boolean | undefined, fixed: number): number | string => (fill ? '100%' : fixed)

// A noted bucket's marker: a filled dot on the top rule with the note text on hover. Drawn as
// a ReferenceLine label so it tracks the category axis instead of being positioned by hand.
function NoteMarker({ viewBox, text }: { viewBox?: { x?: number; y?: number }; text: string }) {
  const x = viewBox?.x ?? 0
  const y = viewBox?.y ?? 0
  return (
    <g style={{ pointerEvents: 'all' }}>
      <title>{text}</title>
      <circle cx={x} cy={y + 4} r={4.5} fill={brand.tealDark} stroke={brand.surface} strokeWidth={1.5} />
    </g>
  )
}

// Category labels on a horizontal bar are right-anchored at the axis, so a long
// raw value (e.g. "ApplePayViaCheckoutCom") overflows the gutter and gets clipped.
// Truncate to what fits the gutter with an ellipsis; the full value stays available
// on hover via the SVG <title>. Width-aware so it adapts to each chart's gutter.
function CategoryTick(props: {
  x?: number
  y?: number
  payload?: { value?: string | number }
  axisWidth?: number
}) {
  const { x = 0, y = 0, payload, axisWidth = 130 } = props
  const full = String(payload?.value ?? '')
  const maxChars = Math.max(6, Math.floor((axisWidth - 12) / 6.4))
  const label = full.length > maxChars ? `${full.slice(0, maxChars - 1).trimEnd()}…` : full
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fill={brand.text} fontSize={12} fontFamily={brand.font}>
      {label !== full && <title>{full}</title>}
      {label}
    </text>
  )
}

// MP-OS-207 — a partial bar keeps its colour but is lighter with a dashed outline; a full bar
// gets exactly the props it always had.
function barCellProps(d: ChartDatum, color: string) {
  return isPartialPoint(d) ? { fill: color, stroke: color, ...PARTIAL_BAR } : { fill: color }
}

function BarH({ chart, fill, animate, palette = 'metric', tooltipLabels, axisFormatter, valueFormatter }: Props) {
  const axisFmt = useAxisFormatter(chart, axisFormatter)
  return (
    <ResponsiveContainer width="100%" height={H(fill, Math.max(140, chart.data.length * 38 + 24))}>
      <BarChart data={chart.data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
        <CartesianGrid stroke={gridStroke} horizontal={false} />
        <XAxis type="number" tick={axisTick} tickLine={false} axisLine={{ stroke: brand.grey }} tickFormatter={axisFmt} />
        <YAxis type="category" dataKey="name" tick={<CategoryTick axisWidth={144} />} tickLine={false} axisLine={false} width={144} />
        <Tooltip cursor={{ fill: 'rgba(21,154,156,0.08)' }} content={<CustomTooltip labels={tooltipLabels} formatValue={valueFormatter} />} />
        {chart.ref != null && <ReferenceLine x={chart.ref} stroke={brand.greyText} strokeDasharray="4 4" />}
        <Bar dataKey="value" name={singleSeriesName(chart.unit)} radius={[0, 4, 4, 0]} maxBarSize={30} isAnimationActive={animate ?? !fill} animationDuration={ANIM}>
          {chart.data.map((d) => (
            <Cell key={d.name} {...barCellProps(d, cellColor(d.name, palette))} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

function BarV({ chart, fill, animate, palette = 'metric', axisFormatter, valueFormatter }: Props) {
  const axisFmt = useAxisFormatter(chart, axisFormatter)
  return (
    <ResponsiveContainer width="100%" height={H(fill, 280)}>
      <BarChart data={chart.data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
        <CartesianGrid stroke={gridStroke} vertical={false} />
        <XAxis dataKey="name" tick={axisTick} tickLine={false} axisLine={{ stroke: brand.grey }} interval={0} />
        <YAxis tick={axisTick} tickLine={false} axisLine={false} width={52} tickFormatter={axisFmt} />
        <Tooltip cursor={{ fill: 'rgba(21,154,156,0.08)' }} content={<CustomTooltip formatValue={valueFormatter} />} />
        {chart.ref != null && <ReferenceLine y={chart.ref} stroke={brand.greyText} strokeDasharray="4 4" />}
        <Bar dataKey="value" name={singleSeriesName(chart.unit)} radius={[4, 4, 0, 0]} maxBarSize={64} isAnimationActive={animate ?? !fill} animationDuration={ANIM}>
          {chart.data.map((d) => (
            <Cell key={d.name} {...barCellProps(d, cellColor(d.name, palette))} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

function GroupedH({ chart, fill, animate, palette = 'metric', axisFormatter, valueFormatter }: Props) {
  const axisFmt = useAxisFormatter(chart, axisFormatter)
  const seriesKeys = chart.series ?? []
  const hasPartial = chart.data.some(isPartialPoint)
  const fixed = Math.max(160, chart.data.length * (18 + seriesKeys.length * 16) + 32)
  return (
    <ResponsiveContainer width="100%" height={H(fill, fixed)}>
      <BarChart data={chart.data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
        <CartesianGrid stroke={gridStroke} horizontal={false} />
        <XAxis type="number" tick={axisTick} tickLine={false} axisLine={{ stroke: brand.grey }} tickFormatter={axisFmt} />
        <YAxis type="category" dataKey="name" tick={<CategoryTick axisWidth={132} />} tickLine={false} axisLine={false} width={132} />
        <Tooltip cursor={{ fill: 'rgba(21,154,156,0.08)' }} content={<CustomTooltip formatValue={valueFormatter} />} />
        <Legend wrapperStyle={{ fontFamily: brand.font, fontSize: 12 }} formatter={legendFormatter(emptySeries(chart.data, seriesKeys))} />
        {seriesKeys.map((key, i) => {
          const color = palette === 'brand' ? brandColor(key) : dataPalette[i % dataPalette.length]
          return (
            <Bar key={key} dataKey={key} name={key} fill={color} radius={[0, 3, 3, 0]} maxBarSize={18} isAnimationActive={animate ?? !fill} animationDuration={ANIM}>
              {hasPartial && chart.data.map((d) => <Cell key={d.name} {...barCellProps(d, color)} />)}
            </Bar>
          )
        })}
      </BarChart>
    </ResponsiveContainer>
  )
}

function Trend({ chart, fill, animate, palette = 'metric', ownBrands = OWN_BRANDS, onPointClick, noteBuckets, tooltipLabels, axisFormatter, valueFormatter = formatNumber }: Props) {
  const axisFmt = useAxisFormatter(chart, axisFormatter)
  // A single data point renders as a lone "tiny weird dot" (Olof) with no line. Show the
  // value as a readable callout instead, and say the span is too short for a trend.
  if ((chart.data?.length ?? 0) <= 1) {
    const only = chart.data?.[0]
    return (
      <div style={{ height: H(fill, 280), display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: brand.font, color: brand.greyText }}>
        {only ? (
          <>
            {(chart.series?.length ? chart.series : ['value']).map((key) => (
              <div key={key}>
                {key !== 'value' && <span>{key}: </span>}
                <span style={{ fontSize: 30, fontWeight: 800, color: brand.teal, fontVariantNumeric: 'tabular-nums' }}>
                  {typeof only[key] === 'number' ? valueFormatter(only[key] as number) : 'No data'}
                </span>
              </div>
            ))}
            <div style={{ fontSize: 13 }}>{String(only.name)} · {chart.unit}</div>
            <div style={{ fontSize: 12 }}>Too few data points for a trend — showing the single value.</div>
          </>
        ) : (
          <div style={{ fontSize: 13 }}>No data for this period.</div>
        )}
      </div>
    )
  }
  // Multi-series (e.g. XQS vs Zyn/Velo): emphasis follows OWNERSHIP, not order — own
  // brands thick, solid and each in its own saturated colour; competitors thin, dashed
  // and on one neutral tone (see charts/emphasis.ts).
  const multi = !!chart.series && chart.series.length > 0
  const anim = animate ?? !fill
  const styles =
    multi && palette === 'brand' ? seriesStyles(chart.series!, ownBrands, dataPalette) : null
  // MP-OS-207 — with partial periods each series is drawn twice: solid through the full
  // periods, dashed (same colour) from the last full point into the partial ones. Null when
  // there is no partial period, so such a chart renders exactly as before.
  const lineKeys = multi ? chart.series! : ['value']
  const split = splitPartialLine(chart.data, lineKeys)
  const lineStyle = (key: string, i: number) => {
    const st = styles?.[i]
    return {
      color: st ? st.color : multi ? dataPalette[i % dataPalette.length] : brand.teal,
      width: st ? st.width : multi ? 2 : 2.5,
      dash: st?.dash,
      opacity: st ? st.opacity : multi ? 1 : undefined,
      dot: st ? st.dot : !multi,
      name: multi ? key : singleSeriesName(chart.unit),
    }
  }
  return (
    <ResponsiveContainer width="100%" height={H(fill, 280)}>
      <LineChart
        data={split ?? chart.data}
        margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
        style={onPointClick ? { cursor: 'pointer' } : undefined}
        onClick={onPointClick ? (e) => {
          // recharts hands back the category the click landed on; ignore a click that
          // missed the plot area (activeLabel undefined) rather than annotating nothing.
          const bucket = (e as { activeLabel?: string } | null)?.activeLabel
          if (bucket) onPointClick(String(bucket))
        } : undefined}
      >
        <CartesianGrid stroke={gridStroke} vertical={false} />
        <XAxis dataKey="name" tick={axisTick} tickLine={false} axisLine={{ stroke: brand.grey }} />
        <YAxis tick={axisTick} tickLine={false} axisLine={false} width={56} tickFormatter={axisFmt} />
        {/* The solid and dashed part of a series share its name; where both have a value (the
            last full point) the tooltip shows it once. */}
        <Tooltip content={<CustomTooltip labels={tooltipLabels} formatValue={valueFormatter} />}
                 payloadUniqBy={split ? (entry) => String(entry.name) : undefined} />
        {chart.ref != null && <ReferenceLine y={chart.ref} stroke={brand.greyText} strokeDasharray="4 4" />}
        {/* Annotated buckets (MP-OS-136). Only the ones actually plotted get a marker, so a
            note outside the visible window doesn't draw a line at the edge of the chart. */}
        {noteBuckets && chart.data.map((d) => {
          const note = noteBuckets.get(String(d.name))
          return note ? (
            <ReferenceLine
              key={`note-${d.name}`}
              x={String(d.name)}
              stroke={brand.tealDark}
              strokeDasharray="3 3"
              strokeOpacity={0.55}
              label={<NoteMarker text={note} />}
            />
          ) : null
        })}
        {/* Legend only when there's more than one series — a single-series chart (e.g. the
            market-share curve) already names its article in the card subtitle, so a one-row
            legend under the curve is redundant (Olof 31/8). */}
        {chart.series && chart.series.length > 1 && <Legend wrapperStyle={{ fontFamily: brand.font, fontSize: 12 }}
          formatter={legendFormatter(emptySeries(chart.data, chart.series), tooltipLabels)} />}
        {/* Every Line must be a DIRECT child of LineChart — recharts
            inspects children by type and does NOT recurse into a Fragment, so a
            <Line> wrapped in <>…</> renders blank (no series, no y-domain). */}
        {lineKeys.map((key, i) => {
          const ls = lineStyle(key, i)
          return (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={ls.name}
              stroke={ls.color}
              strokeWidth={ls.width}
              strokeDasharray={ls.dash}
              strokeOpacity={ls.opacity}
              dot={ls.dot ? { r: 3, fill: ls.color } : false}
              activeDot={{ r: 5 }}
              isAnimationActive={anim}
              animationDuration={ANIM}
            />
          )
        })}
        {/* The partial segments: dashed for a solid series; a dashed (competitor) series keeps
            its own pattern and is lighter instead. A partial point is a hollow dot. Not in
            the legend. */}
        {split && lineKeys.map((key, i) => {
          const ls = lineStyle(key, i)
          return (
            <Line
              key={partialKey(key)}
              type="monotone"
              dataKey={partialKey(key)}
              name={ls.name}
              legendType="none"
              stroke={ls.color}
              strokeWidth={ls.width}
              strokeDasharray={ls.dash ?? PARTIAL_DASH}
              strokeOpacity={ls.dash ? (ls.opacity ?? 1) * PARTIAL_OPACITY : ls.opacity}
              dot={(props: { key?: string; cx?: number; cy?: number; payload?: ChartDatum }) => (
                isPartialPoint(props.payload) && typeof props.cx === 'number' && typeof props.cy === 'number'
                  ? <circle key={props.key} className="partial-dot" cx={props.cx} cy={props.cy} r={3.5} fill={brand.surface} stroke={ls.color} strokeWidth={1.5} />
                  : <g key={props.key} />
              )}
              activeDot={{ r: 5 }}
              isAnimationActive={anim}
              animationDuration={ANIM}
            />
          )
        })}
      </LineChart>
    </ResponsiveContainer>
  )
}

function donutData(data: ChartDatum[], max = 6): ChartDatum[] {
  const sorted = [...data].sort((a, b) => Number(b.value) - Number(a.value))
  if (sorted.length <= max) return sorted
  const head = sorted.slice(0, max)
  const tail = sorted.slice(max).reduce((sum, d) => sum + Number(d.value), 0)
  return [...head, { name: 'Others', value: tail }]
}

// "Us vs the field": the own brand pops in the accent; everyone else recedes into a
// graded slate ramp (dark→pale by rank) so slices are distinguishable but quiet —
// far cleaner than a wash of one identical grey.
const FIELD_RAMP = ['#3f5457', '#5e7376', '#849799', '#aab9ba', '#cdd8d8']

function donutColors(data: ChartDatum[], palette: 'brand' | 'metric'): string[] {
  if (palette !== 'brand') return data.map((_, i) => dataPalette[i % dataPalette.length])
  let field = 0
  return data.map((d) => {
    if (d.name === 'Others') return FIELD_RAMP[FIELD_RAMP.length - 1]
    if (brandColor(d.name) === dataPalette[0]) return dataPalette[0] // own brand → accent
    return FIELD_RAMP[Math.min(field++, FIELD_RAMP.length - 1)]
  })
}

function DonutLegend({ data, colors }: { data: ChartDatum[]; colors: string[] }) {
  const total = data.reduce((s, d) => s + Number(d.value), 0) || 1
  return (
    <ul className="donut-legend">
      {data.map((d, i) => (
        <li key={d.name}>
          <span className="donut-dot" style={{ background: colors[i] }} />
          <span className="donut-name">{d.name}</span>
          <span className="donut-pct">{formatShare((Number(d.value) / total) * 100, { digits: 0 })}</span>
        </li>
      ))}
    </ul>
  )
}

function Donut({ chart, fill, animate, palette = 'metric' }: Props) {
  const data = donutData(chart.data)
  const colors = donutColors(data, palette)
  return (
    <ResponsiveContainer width="100%" height={H(fill, 300)}>
      <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
        <Tooltip content={<CustomTooltip />} />
        {/* Clean legend with the % — replaces scattered, low-contrast slice labels. */}
        <Legend content={<DonutLegend data={data} colors={colors} />} />
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius="58%"
          outerRadius={fill ? '82%' : '88%'}
          paddingAngle={2}
          stroke="var(--surface)"
          strokeWidth={2}
          isAnimationActive={animate ?? !fill}
          animationDuration={ANIM}
          label={false}
        >
          {data.map((d, i) => (
            <Cell key={d.name} fill={colors[i]} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  )
}

// Screen-reader equivalent of the chart: recharts renders to SVG with no accessible
// data, so we mark the visual as decorative (aria-hidden) and expose the same numbers
// as an off-screen data table (AUDIT D-a11y). Shape comes from the pure chartTable()
// helper so the "every datum + series exposed" guarantee is unit-tested (a11y.test.ts).
function ChartDataTable({ chart }: { chart: Chart }) {
  const { columns, rows } = chartTable(chart)
  return (
    <table className="sr-only">
      <caption>{`Chart data (${chart.unit})`}</caption>
      <thead>
        <tr>
          <th scope="col">Category</th>
          {columns.map((c) => <th key={c} scope="col">{c}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.head}>
            <th scope="row">{r.head}</th>
            {r.cells.map((cell, i) => <td key={columns[i]}>{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function InsightChart({ chart: raw, fill, animate, palette, ownBrands = OWN_BRANDS, emptyMessage, onPointClick, noteBuckets, tooltipLabels, axisFormatter, valueFormatter }: Props) {
  // Preserve every period, including real zeroes and missing cells. A renderer
  // cannot infer unavailable coverage from a zero. Only normalize month labels.
  const chart = raw.type === 'line' ? withTrendLabels(raw) : raw
  // Only use own-brand emphasis when an actual own brand is present. A comparison of
  // cities (Malmö/Stockholm) also has multiple series; treating those names as brands
  // made both bars the same neutral grey and obscured the comparison.
  const p = chartPalette(chart, palette, ownBrands)
  // MPD-132: a percent chart's tooltip shows a tiny share as "<0.01", never "0".
  const valueFmt = valueFormatter ?? (isPercentUnit(chart.unit) ? formatShareValue : undefined)
  const partials = raw.data.filter((point) => point.period_kind === 'partial')
  const incomplete = raw.data.filter(isIncompletePoint)
  // MPD-133 L7: derived points that include an incomplete bucket are counted, not listed one by one.
  const incompleteOwn = incomplete.filter((point) => !isDerivedIncomplete(point))
  const incompleteDerived = incomplete.length - incompleteOwn.length
  // A valid query returning zero rows must read as an explicit empty state, never a blank
  // grid with a unit label — a silently empty chart looks "right" and misleads (the same
  // rule the catalog cards already follow).
  if (!chart.data?.length) {
    return (
      <figure className={`chart-card ${fill ? 'fill' : ''}`} aria-label={`Empty chart (${chart.unit})`}>
        <div className="chart-unit">{chart.unit}</div>
        <div className="chart-area chart-empty">{emptyMessage ?? 'No data for this period.'}</div>
      </figure>
    )
  }
  // Unknown/long-tail chart type (there is NO Vega-Lite fallback — AUDIT #9): fail
  // honestly with a visible message rather than silently rendering an empty chart area.
  if (!isSupportedChartType(chart.type)) {
    return (
      <figure className={`chart-card ${fill ? 'fill' : ''}`} aria-label={unsupportedChartMessage(chart.type)}>
        <div className="chart-unit">{chart.unit}</div>
        <div className="chart-area chart-empty" role="alert">{unsupportedChartMessage(chart.type)}</div>
      </figure>
    )
  }
  if (chart.type === 'donut' && (chart.data.some((d) => typeof d.value !== 'number' || !Number.isFinite(d.value) || d.value < 0)
    || chart.data.reduce((sum, d) => sum + Number(d.value), 0) <= 0)) {
    return (
      <figure className={`chart-card ${fill ? 'fill' : ''}`} aria-label={chartAriaLabel(chart)}>
        <div className="chart-unit">{chart.unit}</div>
        <div className="chart-area chart-empty" role="status">A share chart requires a positive total and complete, non-negative values. No shares are inferred.</div>
        <ChartDataTable chart={chart} />
      </figure>
    )
  }
  return (
    <figure className={`chart-card ${fill ? 'fill' : ''}`} aria-label={chartAriaLabel(chart)}>
      <div className="chart-unit">{chart.unit}</div>
      {partials.length > 0 && <figcaption style={{ fontSize: 11, lineHeight: 1.4, color: brand.greyText, padding: '0 8px 8px', flexShrink: 0 }}>
        Partial periods: {partials.map((point) => `${point.name} (${point.period_from ?? '?'} – ${point.period_to ?? '?'})`).join('; ')}
        {/* MPD-64: an ⓘ on screen; written out in exported files (see EXPORTING_ATTR). */}
        <InfoTip label="About the partial periods in this chart" size="small">Do not compare their totals with full periods. A dashed line or lighter bar marks a partial period.</InfoTip>
      </figcaption>}
      {incomplete.length > 0 && <figcaption style={{ fontSize: 11, lineHeight: 1.4, color: brand.greyText, padding: '0 8px 8px', flexShrink: 0 }}>
        {/* MPD-141 Info: the count of derived values that include an incomplete period is in the ⓘ only. */}
        Incomplete data: {incompleteOwn.length ? incompleteOwn.map((point) => `${point.name} (${missingDaysText(missingDays(point))})`).join('; ')
          : 'some values include an incomplete period'}
        <InfoTip label="About the incomplete periods in this chart" size="small">{incompleteDerived > 0 && `${incompleteDerived} cumulative, rolling or indexed ${incompleteDerived === 1 ? 'value includes' : 'values include'} an incomplete period. `}Some days in these periods are not verified yet (for example a delayed or failed data load). Each value is the sum of the verified days only, so it is lower than or equal to the final figure. A 0 is a verified day without sales; a missing day is never counted as zero and shows as a gap. A dashed line or lighter bar marks an incomplete period. A cumulative, rolling-average or indexed value that includes an incomplete period is marked the same way.</InfoTip>
      </figcaption>}
      <div className="chart-area" aria-hidden="true">
        {chart.type === 'barH' && <BarH chart={chart} fill={fill} animate={animate} palette={p} tooltipLabels={tooltipLabels} axisFormatter={axisFormatter} valueFormatter={valueFmt} />}
        {chart.type === 'barV' && <BarV chart={chart} fill={fill} animate={animate} palette={p} axisFormatter={axisFormatter} valueFormatter={valueFmt} />}
        {chart.type === 'groupedH' && <GroupedH chart={chart} fill={fill} animate={animate} palette={p} axisFormatter={axisFormatter} valueFormatter={valueFmt} />}
        {chart.type === 'line' && (
          <Trend chart={chart} fill={fill} animate={animate} palette={p} ownBrands={ownBrands}
                 onPointClick={onPointClick} noteBuckets={noteBuckets} tooltipLabels={tooltipLabels}
                 axisFormatter={axisFormatter} valueFormatter={valueFmt} />
        )}
        {chart.type === 'donut' && <Donut chart={chart} fill={fill} animate={animate} palette={p} />}
      </div>
      <ChartDataTable chart={chart} />
    </figure>
  )
}
